import React from "react";
import { FileText, AlertCircle, CheckCircle, Mail, Phone } from "lucide-react";
import Card from "../modern-components/ui/Card";

const sections = [
  {
    title: "1. Acceptance of Terms",
    content: [
      "By accessing or using the lohamart platform, you agree to be bound by these Terms & Conditions.",
      "If you do not agree, you must discontinue use of the platform immediately.",
      "These terms apply to all visitors, registered users, and business customers.",
    ],
  },
  {
    title: "2. Account Registration",
    content: [
      "You must be at least 18 years old and a duly authorized representative of your business to register.",
      "You are responsible for maintaining the confidentiality of your account credentials.",
      "You agree to provide accurate, current, and complete information during registration.",
      "lohamart reserves the right to suspend or terminate accounts that violate these terms.",
    ],
  },
  {
    title: "3. Products & Pricing",
    content: [
      "All product listings, specifications, and prices are subject to change without prior notice.",
      "Prices displayed are exclusive of applicable taxes unless stated otherwise.",
      "Availability of products is subject to stock levels and supplier conditions.",
      "lohamart reserves the right to cancel orders if a pricing error is identified.",
    ],
  },
  {
    title: "4. Orders & Payments",
    content: [
      "Orders are confirmed only upon receipt of full payment or approved credit terms.",
      "We accept bank transfers, UPI, and other approved payment methods.",
      "By placing an order, you confirm that you have the authority to make purchases on behalf of your business.",
      "lohamart is not liable for payment failures due to third-party gateway issues.",
    ],
  },
  {
    title: "5. Delivery & Logistics",
    content: [
      "Estimated delivery timelines are indicative and may vary due to logistics or supply-chain factors.",
      "Risk of loss or damage transfers to the buyer upon delivery to the specified address.",
      "Any discrepancies in delivered goods must be reported within 48 hours of receipt.",
    ],
  },
  {
    title: "6. Intellectual Property",
    content: [
      "All content on this platform — including text, images, logos, and software — is owned by or licensed to lohamart.",
      "You may not reproduce, distribute, or create derivative works without explicit written permission.",
      "Product images are for illustrative purposes; actual products may vary slightly.",
    ],
  },
  {
    title: "7. Limitation of Liability",
    content: [
      "lohamart shall not be liable for indirect, incidental, or consequential damages arising from use of the platform.",
      "Our total liability for any claim shall not exceed the value of the order in question.",
      "We do not guarantee uninterrupted, error-free access to the platform.",
    ],
  },
  {
    title: "8. Governing Law",
    content: [
      "These Terms are governed by the laws of India.",
      "Any disputes shall be subject to the exclusive jurisdiction of courts in Gujarat, India.",
      "Parties shall attempt to resolve disputes amicably before initiating legal proceedings.",
    ],
  },
  {
    title: "9. Amendments",
    content: [
      "lohamart may modify these Terms at any time. Continued use of the platform constitutes acceptance.",
      "We will notify registered users of significant changes via email.",
    ],
  },
];

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-white pt-40 pb-16">
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary-600 to-secondary-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full border border-white/20 mb-6">
            <FileText className="w-4 h-4" />
            <span className="text-sm font-semibold tracking-wide">LEGAL</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Terms & Conditions
          </h1>
          <p className="text-lg text-gray-100">
            Please read these terms carefully before using the lohamart
            platform.
          </p>
          <p className="text-sm text-gray-300 mt-3">
            Last updated: February 2025
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        {/* Notice */}
        <Card className="mb-8 border-l-4 border-yellow-400 bg-yellow-50">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-gray-700 leading-relaxed">
              These Terms & Conditions constitute a legally binding agreement
              between you and
              <strong> lohamart India Private Limited</strong>. By creating an
              account or placing an order, you acknowledge that you have read
              and accepted these terms in full.
            </p>
          </div>
        </Card>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section) => (
            <Card key={section.title} className="border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-100 pb-3">
                {section.title}
              </h2>
              <ul className="space-y-2">
                {section.content.map((item, i) => (
                  <li key={i} className="flex items-start space-x-3">
                    <CheckCircle className="w-4 h-4 text-primary-500 flex-shrink-0 mt-1" />
                    <span className="text-gray-700 leading-relaxed">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>

        {/* Contact */}
        <Card className="mt-8 bg-gradient-to-br from-primary-600 to-secondary-600 text-white border-0">
          <h2 className="text-xl font-bold mb-3">
            Questions About These Terms?
          </h2>
          <p className="text-gray-200 mb-4">
            For any queries regarding these Terms & Conditions, contact our
            legal team:
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center space-x-2">
              <Mail className="w-5 h-5 text-yellow-300" />
              <span>info@lohamart.in</span>
            </div>
            <div className="flex items-center space-x-2">
              <Phone className="w-5 h-5 text-yellow-300" />
              <span>+91 94370 45109</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TermsConditions;
